import { flow, isEqual, isNull, isUndefined, unset } from 'lodash/fp';
import { all, call, debounce, fork, put, select, takeLatest, takeLeading } from 'redux-saga/effects';
import { toast } from 'react-toastify';

import { deleteClientCalculatorPrompt, fetchClientCalculatorPrompts, updateClientCalculatorPromptPriorityOrder, upsertClientCalculatorPrompt } from '../../../../services/calculator';
import { deleteClientCalculatorPromptFailed, deleteClientCalculatorPromptSuccessful, fetchClientCalculatorPromptsFailed, fetchClientCalculatorPromptsSuccessful, saveClientPromptOrderFailed, saveClientPromptOrderSuccessful, setClientCalculatorPromptUpdated, upsertClientCalculatorPromptFailed, upsertClientCalculatorPromptSuccessful } from './actions';
import { initialCalculatorPrompt } from './reducer';
import { getAllClientCalculatorPrompts, getClientCalculatorPromptToDelete, getCurrentClientCalculatorPromptOrderForType, getSelectedClientCalculatorPrompt } from './selectors';
import { ClientCalculatorActionTypes } from './types';
import { CalculatorPromptDB, CalculatorPromptPriorityOrder, CalculatorPromptUI } from '../../calculator/store/types';

export function* attemptFetchClientCalculatorPrompts() {
    try {
        const calculatorPrompts: CalculatorPromptDB[] = yield call(fetchClientCalculatorPrompts);
        yield put(fetchClientCalculatorPromptsSuccessful(calculatorPrompts));
    } catch (e) {
        yield put(fetchClientCalculatorPromptsFailed((e as Error).message));
    }
}

function* fetchClientCalculatorPromptsWatcher() {
    yield takeLeading(ClientCalculatorActionTypes.FETCH_CLIENT_CALCULATOR_PROMPTS_STARTED, attemptFetchClientCalculatorPrompts);
}

export function* attemptUpsertClientCalculatorPrompt() {
    try {
        const calculatorPrompt: CalculatorPromptUI = yield select(getSelectedClientCalculatorPrompt);
        const calculatorPrompts: CalculatorPromptDB[] = yield call(upsertClientCalculatorPrompt, calculatorPrompt);
        yield put(upsertClientCalculatorPromptSuccessful(calculatorPrompts));
    } catch (e) {
        yield put(upsertClientCalculatorPromptFailed((e as Error).message));
    }
}

function* upsertClientCalculatorPromptWatcher() {
    yield takeLatest(ClientCalculatorActionTypes.UPSERT_CLIENT_CALCULATOR_PROMPT_STARTED, attemptUpsertClientCalculatorPrompt);
}

export function* attemptDeleteClientCalculatorPrompt() {
    try {
        let updatedCalculatorPrompts: CalculatorPromptDB[] = [];
        const calculatorPromptToDelete: CalculatorPromptDB | null = yield select(getClientCalculatorPromptToDelete);
        if (!isNull(calculatorPromptToDelete)) {
            const { promptId, calculatorFieldType, priorityOrder } = calculatorPromptToDelete;
            const calculatorPrompts: CalculatorPromptDB[] = yield call(deleteClientCalculatorPrompt, { promptId, calculatorFieldType, priorityOrder });
            updatedCalculatorPrompts = calculatorPrompts;
        }
        yield put(deleteClientCalculatorPromptSuccessful(updatedCalculatorPrompts));
    } catch (e) {
        yield put(deleteClientCalculatorPromptFailed((e as Error).message));
    }
}

function* deleteClientCalculatorPromptWatcher() {
    yield takeLatest(ClientCalculatorActionTypes.DELETE_CLIENT_CALCULATOR_PROMPT_STARTED, attemptDeleteClientCalculatorPrompt);
}

export function* checkClientCalculatorPromptUpdated() {
    const selectedPrompt: CalculatorPromptUI = yield select(getSelectedClientCalculatorPrompt);
    const existingPrompts: CalculatorPromptDB[] = yield select(getAllClientCalculatorPrompts);
    if (!isUndefined(selectedPrompt.promptId)) {
        const existingPrompt = existingPrompts.find(({ promptId }) => promptId === selectedPrompt.promptId);
        if (existingPrompt) {
            const strippedExistingPrompt = flow(
                unset('createdBy'),
                unset('createdByUsername'),
                unset('createdDate'),
                unset('clientId')
            )(existingPrompt);
            const hasUpdated = !isEqual(strippedExistingPrompt, selectedPrompt);
            yield put(setClientCalculatorPromptUpdated(hasUpdated));
        }
    } else {
        const hasUpdated = !isEqual(selectedPrompt, initialCalculatorPrompt);
        yield put(setClientCalculatorPromptUpdated(hasUpdated));
    }
}

function* checkClientCalculatorPromptUpdatedWatcher() {
    yield debounce(200, ClientCalculatorActionTypes.UPDATE_SELECTED_CLIENT_CALCULATOR_PROMPT, checkClientCalculatorPromptUpdated);
}

export function* attemptReorderClientCalculatorPrompts() {
    try {
        const currentPromptOrder: CalculatorPromptDB[] = yield select(getCurrentClientCalculatorPromptOrderForType);
        const promptOrder: CalculatorPromptPriorityOrder[] = currentPromptOrder.map(({ promptId }, index) => ({ promptId, priorityOrder: index + 1 }));
        const calculatorPrompts: CalculatorPromptDB[] = yield call(updateClientCalculatorPromptPriorityOrder, { promptOrder });
        yield put(saveClientPromptOrderSuccessful(calculatorPrompts));
    } catch (e) {
        toast.error('Unable to save the prompt order. Please try again.');
        yield put(saveClientPromptOrderFailed((e as Error).message));
    }
}

function* reorderClientCalculatorPromptsWatcher() {
    yield takeLeading(ClientCalculatorActionTypes.SAVE_CLIENT_PROMPT_ORDER_STARTED, attemptReorderClientCalculatorPrompts);
}

export function* clientCalculatorSaga() {
    yield all([
        fork(fetchClientCalculatorPromptsWatcher),
        fork(upsertClientCalculatorPromptWatcher),
        fork(deleteClientCalculatorPromptWatcher),
        fork(checkClientCalculatorPromptUpdatedWatcher),
        fork(reorderClientCalculatorPromptsWatcher)
    ]);
}
